import { normalizeEventData } from './eventStyling';

export const EVENT_QUERY_PARAM_KEYS = [
  'eventId',
  'eventCode',
  'eventName',
  'mainEventPhoto',
  'clientName',
  'mainText',
  'templateId',
  'token',
  'eventDate',
  'mainTextColor',
  'mainTextEffect',
  'backgroundImageOpacity',
  'titleTextColor',
  'titleTextEffect',
  'titleTextEffectColor',
  'titleFontSize',
  'titleFontFamily',
  'subtitleTextColor',
  'subtitleTextEffect',
  'subtitleTextEffectColor',
  'subtitleFontSize',
  'subtitleFontFamily',
  'theme',
];

/**
 * Extracts URL parameters from the current window location
 * @returns {Object} Object containing all URL parameters
 */
export const extractUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    eventId: urlParams.get('eventId'),
    eventCode: urlParams.get('eventCode'),
    eventName: urlParams.get('eventName'),
    mainEventPhoto: urlParams.get('mainEventPhoto'),
    clientName: urlParams.get('clientName'),
    mainText: urlParams.get('mainText'),
    templateId: urlParams.get('templateId'),
    token: urlParams.get('token'),
    eventDate: urlParams.get('eventDate'),
    mainTextColor: urlParams.get('mainTextColor'),
    mainTextEffect: urlParams.get('mainTextEffect'),
    backgroundImageOpacity: urlParams.get('backgroundImageOpacity'),
    titleTextColor: urlParams.get('titleTextColor'),
    titleTextEffect: urlParams.get('titleTextEffect'),
    titleTextEffectColor: urlParams.get('titleTextEffectColor'),
    titleFontSize: urlParams.get('titleFontSize'),
    titleFontFamily: urlParams.get('titleFontFamily'),
    subtitleTextColor: urlParams.get('subtitleTextColor'),
    subtitleTextEffect: urlParams.get('subtitleTextEffect'),
    subtitleTextEffectColor: urlParams.get('subtitleTextEffectColor'),
    subtitleFontSize: urlParams.get('subtitleFontSize'),
    subtitleFontFamily: urlParams.get('subtitleFontFamily'),
    theme: urlParams.get('theme'),
  };
};

/**
 * Decodes URL-encoded strings
 * @param {string} str - The string to decode
 * @returns {string} Decoded string
 */
export const decodeUrlParam = (str) => {
  if (!str) return null;
  try {
    return decodeURIComponent(str);
  } catch (error) {
    console.warn('Failed to decode URL parameter:', str);
    return str;
  }
};

const decodeJwtPayload = (token) => {
  if (!token) {
    return {};
  }

  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return {};
    }

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    return {};
  }
};

const getFallbackTokenValue = (payload, aliases) => {
  for (const alias of aliases) {
    if (payload?.[alias] !== undefined && payload?.[alias] !== null) {
      return payload[alias];
    }
  }

  return null;
};

const TOKEN_FIELD_ALIASES = {
  eventId: ['eventId', 'e'],
  eventCode: ['eventCode', 'c'],
  eventName: ['eventName', 'n'],
  mainEventPhoto: ['mainEventPhoto', 'p'],
  clientName: ['clientName', 'cn'],
  mainText: ['mainText', 't'],
  templateId: ['templateId', 'tid'],
  token: ['token'],
  eventDate: ['eventDate', 'd'],
  mainTextColor: ['mainTextColor', 'tc'],
  mainTextEffect: ['mainTextEffect'],
  backgroundImageOpacity: ['backgroundImageOpacity'],
  titleTextColor: ['titleTextColor'],
  titleTextEffect: ['titleTextEffect'],
  titleTextEffectColor: ['titleTextEffectColor'],
  titleFontSize: ['titleFontSize'],
  titleFontFamily: ['titleFontFamily'],
  subtitleTextColor: ['subtitleTextColor'],
  subtitleTextEffect: ['subtitleTextEffect'],
  subtitleTextEffectColor: ['subtitleTextEffectColor'],
  subtitleFontSize: ['subtitleFontSize'],
  subtitleFontFamily: ['subtitleFontFamily'],
  theme: ['theme'],
};

const getMergedValue = (rawParams, decodedPayload, key) => {
  const rawValue = rawParams[key];
  if (rawValue !== null && rawValue !== undefined && rawValue !== 'undefined') {
    return rawValue;
  }

  return getFallbackTokenValue(decodedPayload, TOKEN_FIELD_ALIASES[key] || [key]);
};

/**
 * Extracts and decodes URL parameters
 * @returns {Object} Object containing all decoded URL parameters
 */
export const extractAndDecodeUrlParams = () => {
  const rawParams = extractUrlParams();
  const decodedPayload = decodeJwtPayload(rawParams.token);

  const mergedParams = {
    eventId: getMergedValue(rawParams, decodedPayload, 'eventId'),
    eventCode: getMergedValue(rawParams, decodedPayload, 'eventCode'),
    eventName: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'eventName')),
    mainEventPhoto: getMergedValue(rawParams, decodedPayload, 'mainEventPhoto'),
    clientName: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'clientName')),
    mainText: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'mainText')),
    templateId: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'templateId')),
    token: rawParams.token,
    eventDate: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'eventDate')),
    mainTextColor: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'mainTextColor')),
    mainTextEffect: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'mainTextEffect')),
    backgroundImageOpacity: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'backgroundImageOpacity')),
    titleTextColor: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'titleTextColor')),
    titleTextEffect: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'titleTextEffect')),
    titleTextEffectColor: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'titleTextEffectColor')),
    titleFontSize: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'titleFontSize')),
    titleFontFamily: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'titleFontFamily')),
    subtitleTextColor: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'subtitleTextColor')),
    subtitleTextEffect: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'subtitleTextEffect')),
    subtitleTextEffectColor: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'subtitleTextEffectColor')),
    subtitleFontSize: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'subtitleFontSize')),
    subtitleFontFamily: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'subtitleFontFamily')),
    theme: decodeUrlParam(getMergedValue(rawParams, decodedPayload, 'theme')),
  };

  return normalizeEventData(mergedParams);
}; 

export const buildEventQueryParams = (eventData = {}) => {
  const normalized = normalizeEventData(eventData);
  const params = new URLSearchParams();

  EVENT_QUERY_PARAM_KEYS.forEach((key) => {
    if (normalized[key] !== null && normalized[key] !== undefined && normalized[key] !== '') {
      params.set(key, normalized[key]);
    }
  });

  return params;
};